/**
 * orgGate — pilot org allowlist for the Behavioral Assessment feature.
 *
 * The page is only visible to two pilot organizations: Trust Care Organization
 * (cbo_id 1) and Sycamores (cbo_id 292). This is a NAV-VISIBILITY gate only; the
 * backend independently 403s for non-allowed orgs (defense in depth).
 *
 * Allow if the current org id is on the allowlist:
 *   - the comma-separated Vite env allowlist VITE_BEHAVIORAL_ASSESSMENT_CBO_IDS
 *     overrides the default when set, otherwise
 *   - the built-in default { 1, 292 }.
 *
 * Use this single helper in every visibility decision so the rule stays
 * consistent across the CommandPalette, the app.tsx render guard, and any
 * sidebar render.
 */

// Built-in default allowlist (used when the env var is unset).
// 1 = Trust Care Organization, 292 = Sycamores.
const DEFAULT_ALLOWED_CBO_IDS = new Set<number>([1, 292]);

/** Parse the Vite env allowlist into a set of numeric cbo ids (empty if unset). */
function envAllowlist(): Set<number> {
  const raw = import.meta.env.VITE_BEHAVIORAL_ASSESSMENT_CBO_IDS as string | undefined;
  const ids = new Set<number>();
  if (!raw) return ids;
  for (const part of raw.split(',')) {
    const n = parseInt(part.trim(), 10);
    if (Number.isFinite(n)) ids.add(n);
  }
  return ids;
}

/** The effective allowlist: env override when configured, else the built-in default. */
function allowedCboIds(): Set<number> {
  const env = envAllowlist();
  return env.size > 0 ? env : DEFAULT_ALLOWED_CBO_IDS;
}

/** True when a Vite env allowlist is configured (non-empty). */
export function hasEnvAllowlist(): boolean {
  return envAllowlist().size > 0;
}

/**
 * Decide whether the Behavioral Assessment page should be visible for the
 * current organization.
 *
 * @param orgId   current organization id (from getActiveCboId / org context)
 * @param _orgName retained for call-site compatibility; no longer used (id-based gate).
 */
export function isBehavioralAssessmentAllowed(
  orgId: number | null | undefined,
  _orgName?: string | null,
): boolean {
  return orgId != null && allowedCboIds().has(orgId);
}
